import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, Zap, ArrowRight, Eye } from "lucide-react";
import { toast } from "sonner";

export default function CheckinLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clinic, setClinic] = useState(null);
  const [error, setError] = useState(false);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    api.get(`/clinics/${slug}`).then((r) => setClinic(r.data)).catch(() => setError(true));
  }, [slug]);

  useEffect(() => {
    if (user === null) return; // auth still resolving
    if (!clinic) return;
    if (user && user.role === "patient") {
      // Signed-in client: automatically link this Blitzer to their account.
      (async () => {
        try {
          await api.post("/patient/join", null, { params: { clinic_id: clinic.clinic_id } });
          navigate(`/dashboard?clinic=${clinic.clinic_id}`, { replace: true });
        } catch (e) {
          toast.error("We couldn't link this business to your account. Please try again.");
          navigate("/my-companies", { replace: true });
        }
      })();
    } else if (user === false) {
      // Guest: remember which Blitzer to join after they create an account / sign in.
      localStorage.setItem("pending_join", clinic.clinic_id);
    }
  }, [user, clinic, navigate]);

  const joinBusiness = async () => {
    setJoining(true);
    try {
      await api.post("/patient/join", null, { params: { clinic_id: clinic.clinic_id } });
      toast.success(`Joined ${clinic.name}.`);
      navigate(`/dashboard?clinic=${clinic.clinic_id}`, { replace: true });
    } catch (e) {
      toast.error("Could not join this business. Please try again.");
    } finally {
      setJoining(false);
    }
  };

  const isLoggedInPatient = user && user !== false && user.role === "patient";

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 text-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Business not found</h1>
          <p className="mt-2 text-slate-500">This check-in link is not active. Please contact the front desk.</p>
        </div>
      </div>
    );
  }
  if (!clinic) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-[#0F52BA]" /></div>;
  }

  const accent = clinic.accent_color || "#0F52BA";
  const logo = clinic.has_logo ? `${API}/clinic/logo/${clinic.clinic_id}` : null;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-xl shadow-blue-900/5" data-testid="checkin-landing">
        {logo ? (
          <img src={logo} alt={clinic.name} className="mx-auto h-16 w-auto object-contain" />
        ) : (
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl text-white" style={{ backgroundColor: accent }}><Eye className="h-8 w-8" /></span>
        )}
        <span className="mt-6 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider" style={{ backgroundColor: `${accent}18`, color: accent }}>
          <Zap className="h-3.5 w-3.5" /> Blitz check-in
        </span>
        <h1 className="mt-4 text-2xl font-bold tracking-tight text-slate-900">{clinic.name}</h1>
        <p className="mt-2 text-sm text-slate-500">{clinic.tagline || "Complete your check-in and paperwork before you arrive — in about a minute, right from your phone."}</p>
        <Button
          className="mt-8 h-12 w-full text-base text-white hover:opacity-90"
          style={{ backgroundColor: accent }}
          onClick={() => { localStorage.setItem("pending_join", clinic.clinic_id); navigate(`/register?type=user&join=${clinic.clinic_id}`); }}
          data-testid="checkin-start"
        >
          Create your account to check in <ArrowRight className="ml-1.5 h-5 w-5" />
        </Button>
        <button className="mt-4 text-sm text-slate-500 hover:text-slate-700" onClick={() => { localStorage.setItem("pending_join", clinic.clinic_id); navigate(`/login?join=${clinic.clinic_id}`); }} data-testid="checkin-signin">
          I already have an account
        </button>
        <p className="mt-4 text-xs text-slate-400">We'll link {clinic.name} to your account automatically.</p>
      </div>
      <p className="mt-6 text-xs text-slate-400">Secured by BlitzEntry</p>
    </div>
  );
}
